// nestedObject.ts
// ドット区切りのパス文字列を用いて、ネストしたオブジェクトの該当階層のみを
// イミュータブルに更新するためのユーティリティ。
// FE-BUG-12対応方針に基づき、`changePricingAgreementFormValue`内にあった
// 再帰更新ロジックを単体テスト可能な形で切り出したもの。

/**
 * `obj`のうち`path`（例: "a.b.c"）で指定した階層のみを`newValue`に置き換えた
 * 新しいオブジェクトを返す。既存のオブジェクトは変更せず、パス上の各階層は
 * シャローコピーして新規オブジェクトを生成する（イミュータブル更新）。
 *
 * - パスが1階層の場合はトップレベルのキーを上書きする。
 * - パスが2階層以上の場合は、各階層で`{ ...obj, [key]: ... }`の形で
 *   再帰的にコピーしながら最深部まで辿り、最深部のキーにのみ`newValue`を設定する。
 * - 途中の階層が存在しない場合は`{}`として扱う。
 *
 * @param obj 更新対象のオブジェクト
 * @param path 更新項目パス（ドット区切り。例: "a.b.c"）
 * @param newValue 設定する値
 * @returns 更新後の新しいオブジェクト
 */
export const setNestedValue = <T extends Record<string, any>>(obj: T, path: string, newValue: any): T => {
  const keys = path.split('.');// パスをドットで分割して配列にする

  // 1階層だけなら該当箇所だけ上書きして返す。
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: newValue };
  }

  // ネストしているオブジェクトの再帰探索
  const setNested = (target: any, remainingKeys: string[]): any => {
    // 配列を分ける
    const [key, ...rest] = remainingKeys;
    // 現在の項目が最深部ならコピーしつつKeyを更新
    if (rest.length === 0) {
      return { ...target, [key]: newValue };
    }
    // 現在の項目が最深部ではない場合、...targetでキープして再帰関数を再度実行。
    return {
      ...target,
      [key]: setNested(target[key] ?? {}, rest),
    };
  };

  return setNested(obj, keys);
};
